import {
  type MarketSearchFilters,
  type PropertyDetailsResponse,
  type PropertyReportPropertyInsert,
  type PricingPayload,
  type MarketStatsQuery,
  type MarketComparablePayload,
  type SimpleProperty,
  type MarketSearchResponse,
  type CompsPayload,
  type PropertySearchResponse,
  type RevenueManagementPayload,
  CancelablePromise
} from 'common'
import { CancelablePromiseWrapper } from '@/utils'
import { coreClient } from '@/api/coreclient'

const cleanCompsPayload = (data: PropertyDetailsResponse) => {
  return {
    ...data,
    building_availability: data.building_availability.map((availability) => ({
      ...availability,
      price_plans: [],
      history: [],
      availability_periods: []
    }))
  }
}

export default {
  propertySearch(
    q: string,
    filters: {
      lat?: number
      lon?: number
      state?: string
      zipCode?: string
      maxDistance?: number
    }
  ): CancelablePromiseWrapper<PropertySearchResponse[]> {
    return new CancelablePromiseWrapper(
      new CancelablePromise((resolve, reject, onCancel) => {
        coreClient()
          .then((client) => {
            const request = client.property.search(
              q,
              filters.lat,
              filters.lon,
              filters.state,
              filters.zipCode,
              filters.maxDistance
            )
            // Handle cancellation by forwarding it to the underlying request
            onCancel(() => request.cancel())
            request.then(resolve).catch(reject)
          })
          .catch(reject)
      })
    )
  },
  async marketShapeSearch(q: string) {
    const client = await coreClient()
    return client.market.shapeSearch(q).catch((e) => {
      console.error(e)
      return [] as MarketSearchResponse[]
    })
  },
  async shapeSearchByCentroid(
    tag:
      | 'MSA'
      | 'City'
      | 'County'
      | 'Zip Code'
      | 'Neighborhood'
      | 'School District'
      | 'University'
      | 'School',
    lat: number,
    lon: number
  ) {
    const client = await coreClient()
    return client.market.shapeSearchByCentroid(tag, lat, lon).catch((e) => {
      console.error(e)
      return [] as MarketSearchResponse[]
    })
  },
  async details(id: string) {
    const client = await coreClient()
    return client.property.getProperty(id)
  },
  async comparables(
    subject: PropertyDetailsResponse | SimpleProperty,
    params: {
      excludedIds: string[]
      selectedIds: string[]
      topN: number
      renovations?: number
      maxDistance?: number
      minNumberUnits?: number
      maxNumberUnits?: number
      minYearBuilt?: number
      maxYearBuilt?: number
      minNumberStories?: number
      maxNumberStories?: number
      useEmbedding?: boolean
      isAffordable?: boolean
      isSenior?: boolean
      isStudent?: boolean
      maxMilesToUniversity?: number
      maxMilesToHospital?: number
    }
  ) {
    const client = await coreClient()

    const payload: CompsPayload = {
      subject: 'id' in subject ? cleanCompsPayload(subject) : undefined,
      simple_subject: 'id' in subject ? undefined : subject,
      excluded_ids: params.excludedIds,
      selected_ids: params.selectedIds
    } as CompsPayload

    return client.property.comparables(
      payload,
      params.topN,
      undefined,
      params.renovations,
      params.maxDistance,
      undefined,
      params.minNumberUnits,
      params.maxNumberUnits,
      false,
      params.minYearBuilt,
      params.maxYearBuilt,
      false,
      params.minNumberStories,
      params.maxNumberStories,
      false,
      params.useEmbedding,
      params.isAffordable,
      params.isSenior,
      params.isStudent,
      params.maxMilesToUniversity,
      params.maxMilesToHospital
    )
  },
  async expenseBenchmarks(subject: PropertyDetailsResponse, customGPR?: number) {
    const client = await coreClient()
    return client.property.expenseBenchmarks(subject, customGPR)
  },
  async pricing(subject: PricingPayload) {
    const client = await coreClient()
    return client.property.pricing(subject)
  },
  async revenueManagement(subject: RevenueManagementPayload) {
    const client = await coreClient()
    return client.property.revenueManagement(subject)
  },
  async insertProperty(property: PropertyReportPropertyInsert) {
    const client = await coreClient()
    return client.property.postProperty({ property }).catch((e) => {
      console.error(e)
      return null
    })
  },
  async updateProperty(id: string, property: PropertyReportPropertyInsert) {
    const client = await coreClient()
    return client.property.patchProperty(id, { property }).catch((e) => {
      console.error(e)
      return null
    })
  },
  async marketComparables(filters: MarketComparablePayload) {
    const client = await coreClient()
    return client.market.marketComparables(filters).catch((e) => {
      console.error(e)
      return []
    })
  },
  async marketStatistics(subject: MarketStatsQuery) {
    const client = await coreClient()
    return client.market.marketStatistics({ subject }).catch((e) => {
      console.error(e)
      return null
    })
  },
  async marketDemographics(filters: MarketSearchFilters) {
    const client = await coreClient()
    return client.market.marketDemographics(filters).catch((e) => {
      console.error(e)
      return null
    })
  },
  async taxHistory(payload: {
    id: string
    address: string
    city: string
    state: string
    zip_code: string
    lat: number
    lon: number
  }) {
    const client = await coreClient()
    return client.market.taxHistory(payload).catch((e) => {
      console.error(e)
      return null
    })
  }
}
